import { useLayoutEffect, useState, useRef, useCallback } from "react";

/**
 * Custom hook to detect if an element is horizontally scrollable
 * @param element The HTML element to check for horizontal scrollability
 * @returns boolean indicating if the element is horizontally scrollable
 */
export const useIsScrollable = (element: HTMLElement | null) => {
  const [isScrollable, setIsScrollable] = useState(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const checkScrollability = useCallback(() => {
    if (!element) {
      setIsScrollable(false);
      return;
    }

    // Check if element has horizontal overflow
    const hasHorizontalScroll = element.scrollWidth > element.clientWidth;
    setIsScrollable(hasHorizontalScroll);
  }, [element]);

  useLayoutEffect(() => {
    if (!element) return;

    // Initial check
    checkScrollability();

    // Only set up ResizeObserver in non-test environments
    if (
      typeof ResizeObserver !== "undefined" &&
      process.env.NODE_ENV !== "test"
    ) {
      try {
        resizeObserverRef.current = new ResizeObserver(() => {
          checkScrollability();
        });

        resizeObserverRef.current.observe(element);

        // Cleanup
        return () => {
          if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
          }
        };
      } catch (_error) {
        // Fallback if ResizeObserver fails
        console.warn(
          "ResizeObserver not available, scrollability detection may not update on resize",
        );
      }
    }
  }, [checkScrollability, element]);

  return isScrollable;
};
